/**
 * ScanResults.tsx — Table of findings for a given scan.
 *
 * Features:
 *   - Severity colour-coded chips
 *   - Expandable rows showing description and remediation text
 *   - Empty state illustration
 */

import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';

import BlockIcon from '@mui/icons-material/Block';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ShieldIcon from '@mui/icons-material/Shield';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Finding {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'informational';
  resource_id: string;
  resource_type: string;
  remediation: string;
}

interface ScanResultsProps {
  findings: Finding[];
  scanStatus?: string;
  loading?: boolean;
}

// ── Severity styling ──────────────────────────────────────────────────────────

type ChipColor = 'error' | 'warning' | 'info' | 'default';

function severityChip(severity: string): { label: string; color: ChipColor } {
  switch (severity) {
    case 'critical': return { label: 'CRITICAL', color: 'error' };
    case 'high':     return { label: 'HIGH',     color: 'error' };
    case 'medium':   return { label: 'MEDIUM',   color: 'warning' };
    case 'low':      return { label: 'LOW',       color: 'info' };
    default:         return { label: 'INFO',      color: 'default' };
  }
}

// ── Expandable row ────────────────────────────────────────────────────────────

function FindingRow({ finding }: { finding: Finding }) {
  const [open, setOpen] = useState(false);
  const chip = severityChip(finding.severity);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'none' : undefined } }}
        onClick={() => setOpen((o) => !o)}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip label={chip.label} color={chip.color} size="small" sx={{ fontWeight: 700 }} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>{finding.title}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">{finding.resource_type}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {finding.resource_id || '—'}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expandable detail */}
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3, borderLeft: 3, borderColor: 'primary.main', ml: 4, my: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Description</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {finding.description}
              </Typography>
              {finding.remediation && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Remediation</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {finding.remediation}
                  </Typography>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SEV_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, informational: 4 };

type SortableColumn = 'severity' | 'title' | 'resource_type';

export default function ScanResults({ findings, scanStatus, loading = false }: ScanResultsProps) {
  const [sortCol, setSortCol] = useState<SortableColumn>('severity');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (col: SortableColumn) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sorted = [...findings].sort((a, b) => {
    let cmp: number;
    if (sortCol === 'severity') {
      cmp = (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9);
    } else if (sortCol === 'title') {
      cmp = a.title.localeCompare(b.title);
    } else {
      cmp = (a.resource_type || '').localeCompare(b.resource_type || '');
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const inProgress = scanStatus === 'running' || scanStatus === 'pending';

  if (!loading && findings.length === 0) {
    if (inProgress) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
          <CircularProgress size={40} />
          <Typography variant="h6" color="text.secondary">Scan in progress…</Typography>
          <Typography variant="body2" color="text.disabled">
            Findings will appear here once the scan completes.
          </Typography>
        </Box>
      );
    }

    if (scanStatus === 'cancelled') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
          <BlockIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.6 }} />
          <Typography variant="h6" color="text.secondary">Scan was stopped — no findings</Typography>
          <Typography variant="body2" color="text.disabled">
            This scan was cancelled before it completed.
          </Typography>
        </Box>
      );
    }

    if (scanStatus === 'failed') {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
          <BlockIcon sx={{ fontSize: 56, color: 'error.main', opacity: 0.7 }} />
          <Typography variant="h6" color="text.secondary">Scan failed</Typography>
          <Typography variant="body2" color="text.disabled">
            The scan did not complete successfully. Please check your cloud credentials and try again.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        display="flex" flexDirection="column" alignItems="center" justifyContent="center"
        py={6} gap={2}
      >
        <ShieldIcon sx={{ fontSize: 56, color: 'success.main', opacity: 0.7 }} />
        <Typography variant="h6" color="text.secondary">No findings — all clear!</Typography>
        <Typography variant="body2" color="text.disabled">
          This scan returned no security issues.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: 'background.default' } }}>
            <TableCell padding="checkbox" />
            <TableCell sortDirection={sortCol === 'severity' ? sortDir : false}>
              <TableSortLabel active={sortCol === 'severity'} direction={sortCol === 'severity' ? sortDir : 'asc'} onClick={() => handleSort('severity')}>
                Severity
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortCol === 'title' ? sortDir : false}>
              <TableSortLabel active={sortCol === 'title'} direction={sortCol === 'title' ? sortDir : 'asc'} onClick={() => handleSort('title')}>
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortCol === 'resource_type' ? sortDir : false}>
              <TableSortLabel active={sortCol === 'resource_type'} direction={sortCol === 'resource_type' ? sortDir : 'asc'} onClick={() => handleSort('resource_type')}>
                Resource Type
              </TableSortLabel>
            </TableCell>
            <TableCell>Resource ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((f) => (
            <FindingRow key={f.id} finding={f} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
